import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useEffectOnce from 'react-use/lib/useEffectOnce';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';
import { Card } from '../../../../CommonComponents/OPL/Card/Card';
import { Icon } from '../../../../CommonComponents/OPL/Icon/Icon';
import { Step, StepList } from '../../../../CommonComponents/OPL/Stepper';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { AccountStatus, FORM_WIDTH, stepsMap } from '../../AccountOpening.meta';
import { Button } from '../../../../CommonComponents/OPL/Button/Button';
import { FundModal } from '../FundModal/FundModal';
import { ScrollHereOnce } from '../../../../CommonComponents/ScrollHereOnce';
import { useRouteParams } from '../../hooks/useRouteParams';
import { toast } from '../../../../CommonComponents/OPL/Toast/Toast';
import { MESSAGES } from '../../../../CommonConstants';
import { useDraftAccountQuery } from '../../api/useDraftAccountQuery';
import { isConsumerArea as selectIsConsumerArea } from '../../../../CommonSelectors/CommonAreaSelector';
import { useCanAccessPersonalFinances } from '../../hooks/useCanAccessPersonalFinances';
import { Content, StickyFooter } from '../../../../CommonComponents/OPL/StickyFooter/StickyFooter';
import { GliaGlobalStyle } from '../../AccountOpening.styles';
import { useBootstrapConsumerArea } from '../../../../ConsumerArea/hooks/useBootstrapConsumerArea';
import { navigateToConsumerLandingPage } from '../../../../ConsumerArea/utils/navigateToConsumerLandingPage';
import { FullPageLoading } from '../../../../CommonComponents/OPL/FullpageLoading/FullPageLoading';
import { fetchNetWorthData } from '../../../../ContextualSidebar/NetWorthSidebar/NetWorthSidebarActions';
import { ErrorState } from '../../../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';

export const AccountOpeningComplete = () => {
  const { clientId, draftId, isFromWorkflow } = useRouteParams();

  const { data } = useDraftAccountQuery({ clientId, draftId });

  const [isFundModalOpen, setIsFundModalOpen] = useState(false);

  const isConsumerArea = useSelector(selectIsConsumerArea);

  const canAccessPersonalFinances = useCanAccessPersonalFinances();

  const dispatch = useDispatch();

  const {
    mutateAsync: bootstrapConsumerArea,
    isLoading: isBootstrappingConsumerArea,
    isError: isBootstrapConsumerAreaError,
  } = useBootstrapConsumerArea();

  if (!data) {
    return null;
  }

  const {
    accountOpeningStatus,
    data: { customerType },
  } = data;

  if (customerType === null) {
    return null;
  }

  const steps = stepsMap[customerType];

  const onCompleted = async () => {
    if (isConsumerArea && isFromWorkflow) {
      try {
        const appVariables = await bootstrapConsumerArea({
          clientId,
          completedWorkflow: isFromWorkflow,
        });

        const { componentName, modulesToHide } = appVariables;

        // User has access to Personal Finances
        // Note: we cannot use the canAccessPersonalFinances selector here. We need to
        // use the "fresh" appVariables we get from bootstrapping.
        if (componentName !== 'NoClientAccess' && !modulesToHide.includes('CurrentFinances')) {
          await dispatch(fetchNetWorthData(clientId));

          ADV.util.Router.go2('ApexAccount', { clientId, accountId: draftId });
        } else {
          navigateToConsumerLandingPage();
        }
      } catch (err) {
        console.error(err);
        toast.error(MESSAGES.GENERIC_ERROR);
      }
    } else if (isConsumerArea && !canAccessPersonalFinances) {
      navigateToConsumerLandingPage();
    } else {
      ADV.util.Router.go2('ApexAccount', { clientId, accountId: draftId });
    }
  };

  if (accountOpeningStatus !== AccountStatus.Opened) {
    return <AccountNotOpened onCompleted={onCompleted} isError={isBootstrapConsumerAreaError} />;
  }

  return (
    <Box minWidth={864} mt="48">
      <GliaGlobalStyle />

      <ScrollHereOnce options={false} />

      {isBootstrappingConsumerArea && <FullPageLoading />}

      <Box maxWidth={800} mx="auto">
        <Text as="h1" fontSize="h4" fontWeight={500} mb="8">
          Account Opening
        </Text>

        <StepList variant="complete" aria-hidden>
          {steps.map(step => (
            <Step key={step} disabled>
              {step}
            </Step>
          ))}
        </StepList>

        <Content>
          <Card p="48" my="48" width={FORM_WIDTH} mx="auto">
            <Text fontWeight={600} fontSize="h4" color="neutral.600" textAlign="center">
              Account Opening Completed
            </Text>

            <Icon
              className="fas fa-check-circle"
              my="24"
              mx="auto"
              color="success.500"
              display="block"
              fontSize="large"
              textAlign="center"
            />

            <Text as="p" textAlign="center" color="neutral.500">
              Your new account application is approved. You may now fund your account.
            </Text>
          </Card>
        </Content>
      </Box>

      <StickyFooter>
        <Button variant="subtleInactive" onClick={onCompleted} size="large">
          Skip for now
        </Button>

        <Button size="large" onClick={() => setIsFundModalOpen(true)}>
          Add Funds
        </Button>
      </StickyFooter>

      {isFundModalOpen && (
        <FundModal onClose={() => setIsFundModalOpen(false)} onCompleted={onCompleted} />
      )}
    </Box>
  );
};

interface AccountNotOpenedProps {
  isError: boolean;
  onCompleted: () => void;
}

const AccountNotOpened = ({ isError, onCompleted }: AccountNotOpenedProps) => {
  useEffectOnce(() => {
    onCompleted();
  });

  if (isError) {
    return <ErrorState onAction={onCompleted} height="100%" />;
  }

  return <FullPageLoading />;
};
